import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { apiPost } from "@web/lib/api";
import { configQuery } from "@web/lib/session";
import { Button, Card, EmptyState, Page, Stack, Text, TextField } from "@web/ui";

export function SignIn() {
  const navigate = useNavigate();
  // Fails without the Worker (or unsigned, see report) → treat as "no Google, unknown env".
  const config = useQuery(configQuery);
  const googleSignIn = config.data?.googleSignIn ?? false;
  const staging = config.data?.env === "staging";

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<"google" | "magic" | null>(null);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function withGoogle() {
    setBusy("google");
    setError(null);
    try {
      const res = await apiPost<{ url?: string; redirect?: boolean }>("/api/auth/sign-in/social", { provider: "google", callbackURL: "/" });
      if (res.url) window.location.assign(res.url);
      else setError("Google sign-in is not available right now.");
    } catch {
      setError("Google sign-in is not available right now.");
    } finally {
      setBusy(null);
    }
  }

  async function withMagicLink(e: FormEvent) {
    e.preventDefault();
    setBusy("magic");
    setError(null);
    // Always show the same confirmation, whether or not the email is invited.
    await apiPost("/api/auth/sign-in/magic-link", { email: email.trim(), callbackURL: "/" }).catch(() => undefined);
    setBusy(null);
    setSent(true);
  }

  return (
    <Page variant="auth">
      <Stack gap={6} align="center">
        <Stack gap={2} align="center">
          <img src="/icon.svg" alt="" width={64} height={64} />
          <Text variant="large-title" align="center">
            venue365
          </Text>
          <Text variant="callout" tone="secondary" align="center">
            Sign in to your venue workspace
          </Text>
        </Stack>

        <Card className="w-full">
          {sent ? (
            <EmptyState
              icon={Mail}
              title="Check your email for the sign-in link"
              body={`If ${email.trim()} belongs to a team member, a link is on its way. It expires in a few minutes.`}
              action={
                <Button variant="secondary" onClick={() => setSent(false)}>
                  Use a different email
                </Button>
              }
            />
          ) : (
            <Stack gap={4} as="form" onSubmit={withMagicLink}>
              {googleSignIn && (
                <>
                  <Button variant="secondary" loading={busy === "google"} onClick={withGoogle}>
                    Continue with Google
                  </Button>
                  <Text variant="caption" tone="tertiary" align="center">
                    or get a link by email
                  </Text>
                </>
              )}
              <TextField
                label="Work email"
                type="email"
                name="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={error ?? undefined}
              />
              <Button type="submit" loading={busy === "magic"} icon={<Mail aria-hidden size={20} />}>
                Email me a sign-in link
              </Button>
            </Stack>
          )}
        </Card>

        <Text variant="caption" tone="tertiary" align="center">
          Invited team members only. Ask your company admin for access.
        </Text>
        {staging && (
          <Button variant="secondary" fullWidth={false} onClick={() => navigate({ to: "/dev/ui" })}>
            Component gallery (staging)
          </Button>
        )}
      </Stack>
    </Page>
  );
}
